import type ApolloCache from '@wora/apollo-cache'
import type { ContactQuery, Contact, SearchContactsQuery } from '../generated/types'
import { SearchContactsDocument, ContactDocument, FullContactFragmentDoc } from '../generated/types'
import { defaultDataIdFromObject } from 'apollo-cache-inmemory'
import type { CacheProxy } from '.'
import { updateQueries } from './util'

export interface UpdateCachedContactOptions {
  /**
   * Drop this contact from every cached `searchContacts` result, mirroring
   * `UpdateCachedDealOptions.removeFromLists` — for a soft delete/merge that doesn't set
   * `_deleted`, so callers don't have to lie about that tombstone flag just to hide a row.
   */
  removeFromLists?: boolean
}

/**
 * Pure transform: strip the given ids out of one cached `searchContacts`-shaped page. Mirrors
 * `filterDealRowsByIds` in `cache/deals.ts` — see that function's docblock for why `total_rows`
 * is decremented only by rows actually removed, never below zero.
 */
export function filterContactRowsByIds<
  T extends { contacts?: Array<{ _id: string }> | null; total_rows?: number | null }
>(rows: T, removeIds: string[]): T {
  if (!rows?.contacts) return rows

  const removeSet = new Set(removeIds)
  const filtered = rows.contacts.filter((contact) => !removeSet.has(contact._id))
  const removed = rows.contacts.length - filtered.length

  return {
    ...rows,
    contacts: filtered,
    total_rows: Math.max(0, (rows.total_rows ?? 0) - removed)
  }
}

/**
 * Updates any cached contacts in the apollo cache
 */
export function updateCachedContact(proxy: CacheProxy, contact: Contact, options?: UpdateCachedContactOptions) {
  const cache = proxy as ApolloCache
  cache.writeQuery({
    query: ContactDocument,
    variables: { id: contact._id },
    data: {
      contact: {
        __typename: 'Contact',
        ...contact
      }
    }
  })

  // update all searchContact queries to remove the contact
  if (options?.removeFromLists || contact._deleted) {
    updateQueries<SearchContactsQuery>(cache, {
      query: SearchContactsDocument,
      update: (data) => ({
        ...data,
        searchContacts: filterContactRowsByIds(data.searchContacts, [contact._id])
      })
    })
  }

  cache.writeFragment({
    fragment: FullContactFragmentDoc,
    id: defaultDataIdFromObject({ id: contact._id, __typename: 'Contact' }),
    data: {
      __typename: 'Contact',
      ...contact
    }
  })
}

export function updateCachedContactPartially(
  cache: ApolloCache,
  contactId: string,
  values: Partial<Contact>,
  options?: UpdateCachedContactOptions
) {
  try {
    const contact = cache.readQuery<ContactQuery>({
      query: ContactDocument,
      variables: { id: contactId }
    }).contact

    updateCachedContact(
      cache,
      {
        ...contact,
        ...values
      },
      options
    )
  } catch {
    console.warn('Unable to find contact for id ' + contactId)
  }
}
